"use client";

import { memo } from "react";
import type { MessageReaction } from "@/lib/types/channel";

type ReactionGroup = {
  emoji: string;
  count: number;
  reacted: boolean;
};

function groupReactions(
  reactions: MessageReaction[],
  currentMemberId: string,
): ReactionGroup[] {
  const map = new Map<string, { count: number; reacted: boolean }>();
  for (const r of reactions) {
    const existing = map.get(r.emoji) ?? { count: 0, reacted: false };
    map.set(r.emoji, {
      count: existing.count + 1,
      reacted: existing.reacted || r.memberId === currentMemberId,
    });
  }
  return Array.from(map.entries()).map(([emoji, { count, reacted }]) => ({
    emoji,
    count,
    reacted,
  }));
}

type Props = {
  reactions: MessageReaction[];
  currentMemberId: string;
  onToggle: (emoji: string) => void;
  disabled?: boolean;
};

function MessageReactions({
  reactions,
  currentMemberId,
  onToggle,
  disabled = false,
}: Props) {
  const groups = groupReactions(reactions, currentMemberId);

  if (disabled || groups.length === 0) return null;

  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {groups.map(({ emoji, count, reacted }) => (
        <button
          key={emoji}
          onClick={() => onToggle(emoji)}
          className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors ${
            reacted
              ? "border-indigo-300 bg-indigo-50 text-indigo-700 dark:border-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
              : "border-border bg-secondary text-muted-foreground hover:border-primary/40"
          }`}
        >
          <span>{emoji}</span>
          <span>{count}</span>
        </button>
      ))}
    </div>
  );
}

export default memo(MessageReactions);
